// THE KRAKENS window — dark-water dock. Pick King Octo or Queen Octo, then see that org's needs and give directly.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { X, Minus, ExternalLink, Check, Pencil } from "lucide-react";
import { DraggableTile, tileDefault } from "../../hooks/useDragPosition";
import { useAuth } from "../../App";
import { KrakenChooser, KrakenDetail } from "./KrakenViews";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const abs = (u) => (u?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
export const KRAKON_IMG = "/krakon-badge.webp";
export const KRAKON_ART = "/krakon-art.webp";
export const KRAKON_SURFACED = "/krakon-surfaced.webp";

const Ctx = createContext(null);
export const useKrakon = () => useContext(Ctx);

export const useKrakonData = () => useContext(Ctx)?.data;

export const KrakonProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [pick, setPick] = useState(null); // kraken_id being viewed, null = the chooser
  const [data, setData] = useState(null);
  const refresh = useCallback(() => axios.get(`${API}/krakon`).then((r) => setData(r.data)).catch(() => setData((d) => d || { krakens: [], spot: null })), []);
  useEffect(() => { refresh(); }, [refresh]);
  const openDock = useCallback((kid) => { if (typeof kid === "string") setPick(kid); setOpen(true); setMinimized(false); }, []);
  const minimize = useCallback(() => setMinimized(true), []);
  const close = useCallback(() => { setOpen(false); setMinimized(false); setPick(null); }, []);
  const value = useMemo(() => ({ open, minimized, pick, setPick, openDock, minimize, close, data, refresh }), [open, minimized, pick, openDock, minimize, close, data, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

const tilePos = () => { const p = tileDefault("left")(); return { x: p.x, y: Math.max(8, p.y - 190) }; };

export const WishItem = ({ w, org, compact = false }) => {
  const k = useContext(Ctx);
  return (
  <li id={!compact ? `wish-${w.wish_id}` : undefined} className={`rounded-xl p-3 ${compact ? "bg-white/[0.06] ring-1 ring-[#7FE3D6]/20" : "bg-white ring-1 ring-[#E2E0DC] scroll-mt-28 target:ring-2 target:ring-[#F5D06F]"} ${w.fulfilled ? "opacity-70" : ""}`} data-testid={`wish-${w.wish_id}`}>
    <div className="flex items-start gap-3">
      {w.photo_url && <img src={abs(w.photo_url)} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />}
      <div className="min-w-0 flex-1">
        <p className={`m-0 text-sm font-bold leading-snug ${w.fulfilled ? "line-through" : ""}`}>
          {compact ? <Link to={`${org.page}#wish-${w.wish_id}`} onClick={() => k?.minimize()} className="no-underline hover:underline" style={{ color: "inherit" }} data-testid={`wish-link-${w.wish_id}`}>{w.text}</Link> : w.text}
          {w.fulfilled && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-500/20 text-emerald-300 px-2 py-0.5 text-[10px] font-black align-middle"><Check className="h-3 w-3" /> fulfilled</span>}
        </p>
        {w.why && <p className={`m-0 mt-0.5 text-xs leading-snug ${compact ? "text-[#BFE9E2]/80" : "text-[#5C5C5C]"}`}>{w.why}</p>}
        {w.link && !w.fulfilled && <a href={w.link} target="_blank" rel="noopener noreferrer" className={`mt-1 inline-flex items-center gap-1 text-[11px] font-bold ${compact ? "text-[#7FE3D6]" : "text-[#1f4d3a]"}`} data-testid={`wish-ext-${w.wish_id}`}>Help with this <ExternalLink className="h-3 w-3" /></a>}
      </div>
    </div>
  </li>
  );
};

// Top of the window: both Krakens side by side on the chooser, or the picked Kraken's art.
const DockHeader = ({ k, current, onMinimize, onClose, isAdmin }) => (
  <div className="relative shrink-0">
    {current ? (
      <img src={abs(current.art_url)} alt="" className="h-36 w-full object-cover object-[50%_22%] opacity-95" data-testid="krakon-dock-art" />
    ) : (
      <div className="h-36 w-full flex" data-testid="krakon-dock-art">
        {(k.data?.krakens || []).slice(0, 3).map((x) => (
          <button key={x.kraken_id} type="button" onClick={() => k.setPick(x.kraken_id)} className="relative flex-1 min-w-0 overflow-hidden" aria-label={`Open ${x.title}`} data-testid={`krakon-dock-art-${x.kraken_id}`}>
            <img src={abs(x.art_url)} alt="" className="h-full w-full object-cover object-[50%_22%] opacity-95 transition-transform duration-500 hover:scale-105" />
          </button>
        ))}
      </div>
    )}
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#06202B] via-[#06202B]/40 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 px-4 pb-2 flex items-end justify-between gap-3">
      <div className="min-w-0">
        <p className="m-0 text-[10px] font-black uppercase tracking-[0.3em] text-[#7FE3D6]" data-testid="krakon-dock-kicker">{current ? current.title : "Meet the Krakens"}</p>
        <h2 className="m-0 font-['Playfair_Display'] text-xl font-bold leading-tight line-clamp-2" data-testid="krakon-dock-title">{current ? current.org?.name : "King Octo · Queen Octo"}</h2>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {isAdmin && <Link to="/admin/holiday-hop#krakens" onClick={onMinimize} className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-[#7FE3D6]" title="Edit the Krakens" aria-label="Edit the Krakens" data-testid="krakon-dock-edit"><Pencil className="h-3.5 w-3.5" /></Link>}
        <button type="button" onClick={onMinimize} className="inline-flex items-center gap-1 rounded-full bg-[#7FE3D6] text-[#06202B] px-2.5 py-1 text-[11px] font-black" data-testid="krakon-dock-minimize"><Minus className="h-3.5 w-3.5" /> Minimize</button>
        <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10" aria-label="Close" data-testid="krakon-dock-close"><X className="h-4 w-4" /></button>
      </div>
    </div>
  </div>
);

export const KrakonDock = () => {
  const k = useKrakon();
  const { user } = useAuth() || {};
  const data = useKrakonData();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => { const mq = window.matchMedia("(max-width: 639px)"); const f = (e) => setIsMobile(e.matches); mq.addEventListener("change", f); return () => mq.removeEventListener("change", f); }, []);
  if (!k.open) return null;
  const current = (data?.krakens || []).find((x) => x.kraken_id === k.pick && x.org) || null;
  if (k.minimized) {
    return (
      <DraggableTile posKey="wall_krakon_tile_pos" defaultPos={tilePos} onTap={k.openDock} ariaLabel="Open the Krakens" testId="krakon-minimized-tile" className="!z-[1060] h-[90px] w-[90px] rounded-full overflow-hidden ring-2 ring-[#7FE3D6] shadow-2xl bg-[#06202B]">
        <img src={abs(current?.badge_url || data?.krakens?.[0]?.badge_url || KRAKON_IMG)} alt="" className="h-full w-full object-cover pointer-events-none" draggable={false} />
      </DraggableTile>
    );
  }
  return (
    <>
    {isMobile && <div className="fixed inset-0 z-[79] bg-black/30" onClick={k.close} data-testid="krakon-dock-backdrop" />}
    <div className={`fixed z-[80] krakon-sea text-[#E6FAF7] shadow-2xl ring-1 ring-[#7FE3D6]/40 ${isMobile ? "inset-x-0 bottom-0 rounded-t-3xl max-h-[85vh]" : "left-4 bottom-4 w-[440px] rounded-3xl max-h-[85vh]"} flex flex-col overflow-hidden`} data-testid="krakon-dock">
      <DockHeader k={k} current={current} onMinimize={k.minimize} onClose={k.close} isAdmin={user?.role === "admin"} />
      <div className="flex-1 min-h-0 px-4 pb-5 pt-3 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }} data-testid="krakon-dock-scroll">
        {!data ? <p className="text-sm text-[#BFE9E2]">Surfacing…</p>
          : current ? <KrakenDetail k={current} data={data} onBack={() => k.setPick(null)} onLeave={k.minimize} />
          : data.krakens?.length ? <KrakenChooser data={data} onPick={k.setPick} />
          : <p className="text-sm text-[#BFE9E2]" data-testid="krakon-dock-empty">The Krakens are resting — check back soon.</p>}
      </div>
    </div>
    </>
  );
};
